import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ChatAttachment, } from '../../../models/chat.models';
import { AppPermissionService } from '../../../services/app-permission.service';
import { FileUtilsService } from '../../../services/file-utils.service';
import { MAX_TOTAL_FILE_SIZE_BYTES } from '../../../const/file-extensions';
import { filter, take } from 'rxjs';

@Component({
  selector: 'app-ai-quote-chat-input',
  templateUrl: './ai-quote-chat-input.component.html',
  styleUrl: './ai-quote-chat-input.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AiQuoteChatInputComponent {
  @Input() isMobileApp: boolean = false;
  @Input() isAndroid: boolean = false;
  @Input() disableInput: boolean = false;
  @Input() loading: boolean;
  @Input() submittedFilesSizeBytes: number = 0;

  @Output() toggleMic: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() sendMessgae = new EventEmitter();
  @Output() errorMessageHandler = new EventEmitter<string>();
  @Output() cameraDialogOpenChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('inputElementText', { static: false }) inputElementText: ElementRef<HTMLTextAreaElement>;
  @ViewChild('imageScroller') imageScroller!: ElementRef<HTMLDivElement>;
  @ViewChild('imageViewport') imageViewport!: ElementRef<HTMLDivElement>;
  @ViewChild('fileUpload') private fileUpload?: ElementRef;

  // File Uploads
  showUploadOptions: boolean = false;
  showCameraDialog: boolean = false;
  showLeftArrow: boolean = false;
  showRightArrow: boolean = false;
  inputText: string = ''
  micEnabled: boolean = false;
  selectedFiles: ChatAttachment[] = [];

  constructor(
    private readonly appPermissionService: AppPermissionService,
    private readonly fileUtilsService: FileUtilsService
  ) {
  }

  /**
  * Invoked when a files are selected by the user. (Currently used for Ai quote chat)
  * @param event 
  */
  onFilesSelected(event: any) {
    this.showUploadOptions = false;
    const files: FileList = event.target.files;

    if (!files?.length) {
      return;
    }

    const newFiles = Array.from(files).map(file => {
      const isImage = file.type.startsWith('image/');
      return {
        file,
        preview: URL.createObjectURL(file),
        isImage,
        typeLabel: this.fileUtilsService.getFileTypeLabel(file),
        sizeLabel: this.fileUtilsService.getFileSizeLabel(file.size),
        sizeInBytes: file.size,
        uploadState: 'pending'
      };
    });

    this.appendFilesWithSizeLimit(newFiles);

    if (event?.target) {
      event.target.value = '';
    }

    if (this.selectedFiles.length) {
      this.forceArrowUpdate();
    }
  }

  private appendFilesWithSizeLimit(newFiles: Array<{
    file: File;
    preview: string;
    isImage: boolean;
    typeLabel: string;
    sizeLabel: string;
    sizeInBytes: number;
  }>): void {
    if (!newFiles.length) {
      return;
    }

    let runningTotal = this.totalFilesSizeBytes;
    const acceptedFiles: typeof newFiles = [];
    const rejectedFiles: string[] = [];

    for (const fileItem of newFiles) {
      if (runningTotal + fileItem.sizeInBytes > MAX_TOTAL_FILE_SIZE_BYTES) {
        if (fileItem.preview) {
          URL.revokeObjectURL(fileItem.preview);
        }
        rejectedFiles.push(fileItem.file.name);
        continue;
      }

      acceptedFiles.push(fileItem);
      runningTotal += fileItem.sizeInBytes;
    }

    if (rejectedFiles.length) {
      this.errorMessageHandler.emit('The total files size can\'t exceed 3GB');
    }

    this.selectedFiles = [...this.selectedFiles, ...acceptedFiles];
  }

  get totalSelectedFilesSizeBytes(): number {
    return this.selectedFiles?.reduce((total, item) => total + (item.sizeInBytes || 0), 0);
  }

  get totalFilesSizeBytes(): number {
    return (this.submittedFilesSizeBytes || 0) + this.totalSelectedFilesSizeBytes;
  }

  get isFileUploadLimitReached(): boolean {
    return this.totalFilesSizeBytes >= MAX_TOTAL_FILE_SIZE_BYTES;
  }

  get uploadLimitTooltip(): string {
    return 'Upload Files upto 3 GB in total.';
  }

  onClickUpload(from?: string) {
    // On web, the toggle button also closes the already-open options panel; always allow closing it even at the size limit.
    if (!this.isMobileApp && this.showUploadOptions) {
      this.showUploadOptions = false;
      return;
    }

    if (this.isFileUploadLimitReached) {
      return;
    }
    this.showUploadOptions = !this.showUploadOptions;
    
    // For mobile apps, directly open the file selector or camera based on the option chosen, instead of showing the upload options dialog
    if (this.isMobileApp) {
      this.appPermissionService.receiveAppCameraPermissionStatus()
        .pipe(filter(status => status === true), take(1))
        .subscribe(() => {
          this.onUploadFile();
        });
      this.appPermissionService.requestAppCameraPermissions().next(true);
    }
  }

  onUploadFile(from?: string) {
    if (!this.isMobileApp) {
      this.showUploadOptions = false;
    }

    if (this.isFileUploadLimitReached) {
      return;
    }

    const input = this.fileUpload?.nativeElement;
    if (!input) {
      return;
    }

    input.click();
  }

  onOpenCameraDialog() {
    if (this.isMobileApp) {
      if (this.isFileUploadLimitReached) {
        return;
      }
      this.appPermissionService.receiveAppCameraPermissionStatus().
        pipe(filter(status => status === true), take(1))
        .subscribe(() => {
          this.onUploadFile('camera');
        });
      this.appPermissionService.requestAppCameraPermissions().next(true);
    } else {
      this.showUploadOptions = false;
      if (this.isFileUploadLimitReached) {
        return;
      }
      this.showCameraDialog = true;
      this.cameraDialogOpenChange.emit(true);
    }
  }

  onCloseCameraDialog() {
    this.showCameraDialog = false;
    this.cameraDialogOpenChange.emit(false);
  }

  scrollRight(): void {
    this.imageScroller.nativeElement.scrollBy({ left: 70, behavior: 'smooth' });
    this.forceArrowUpdate();
  }

  scrollLeft(): void {
    this.imageScroller.nativeElement.scrollBy({ left: -70, behavior: 'smooth' });
    this.forceArrowUpdate();
  }

  // Force update of arrow visibility after the view has updated following file addition or removal
  private forceArrowUpdate(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.updateArrowVisibility();
      });
    });
  }

  // Remove a selected file and revoke its preview URL if it's an image
  removeImage(index: number): void {
    const [removedFile] = this.selectedFiles.splice(index, 1);

    if (removedFile?.isImage && removedFile.preview) {
      URL.revokeObjectURL(removedFile.preview);
    }

    if (!this.selectedFiles.length) {
      this.resetFileUpload();
      return;
    }

    this.selectedFiles = [...this.selectedFiles];
    this.forceArrowUpdate();
  }

  // Update the visibility of the left and right arrows based on the scroll position
  updateArrowVisibility(): void {
    if (!this.imageScroller || !this.imageViewport) return;

    const scroller = this.imageScroller.nativeElement;
    const viewport = this.imageViewport.nativeElement;

    this.showLeftArrow = scroller.scrollLeft > 0;

    this.showRightArrow =
      scroller.scrollWidth > viewport.clientWidth &&
      scroller.scrollLeft + viewport.clientWidth < scroller.scrollWidth;
  }

  // Set the input text programmatically and focus the textarea
  setInputText(text: string) {
    this.inputText = text;
    this.focus();
  }

  // Dynamically adjust the height of the textarea based on content, with a max height limit
  onInputChangeTextArea(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    this.inputText = value;
    const textarea = this.inputElementText.nativeElement;
    textarea.style.height = 'auto'; // Reset
    const newHeight = Math.min(textarea.scrollHeight, 120); // Limit to 120px
    textarea.style.height = `${newHeight}px`;
  }

  // Reset file upload state and clear selected files
  resetFileUpload() {
    if (this.fileUpload?.nativeElement) {
      this.fileUpload.nativeElement.value = '';
    }
    this.selectedFiles = [];
    this.clearInput();
  }

  // Clear the input textarea and reset its height
  clearInput() {
    if (this.inputElementText?.nativeElement) {
      const textarea = this.inputElementText.nativeElement;
      textarea.innerHTML = '';
      textarea.value = '';
      textarea.style.height = '36px'; // Reset to min height
    }

    this.inputText = '';
  }

  // Set focus to the input textarea
  focus() {
    this.inputElementText.nativeElement.focus();
  }

  // Handle Enter key press for submitting the message
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); // Prevent newline if needed
      this.onEnter(event);    // Call your existing method
    }
  }

  // Prevent form submission on Enter key press
  onEnter(event: any) {
    event.preventDefault();
  }

  onToggleMic() {
    this.micEnabled = !this.micEnabled;
    this.toggleMic.emit(this.micEnabled);
  }
  
  getInputText(): string {
    return this.inputText;
  }

  getSelectedFiles(): ChatAttachment[] {
    return this.selectedFiles;
  }

  // Submit the msg through the send icon
  sendMessage() {
    this.sendMessgae.emit(this.inputText);
  }

  // Ensure the input textarea is focused and the cursor is set at the end of the text
  setInputCursor() {
    const elementReady = !!this.inputElementText?.nativeElement;

    if (elementReady) {
      this.focus();
    } else {
      setTimeout(() => this.setInputCursor(), 0);
    }
  }

  async onCaptureImage(imageUrl: string) {
    // close dialog
    this.showCameraDialog = false;
    this.cameraDialogOpenChange.emit(false);
    
    const response = await fetch(imageUrl);
    const blob = await response.blob();

    const file = new File(
      [blob],
      `camera-image-${Date.now()}.jpg`,
      { type: blob.type || 'image/jpeg' }
    );

    const attachment: ChatAttachment = {
      file,
      preview: imageUrl,
      isImage: true,
      typeLabel: this.fileUtilsService.getFileTypeLabel(file),
      sizeLabel: this.fileUtilsService.getFileSizeLabel(file.size),
      sizeInBytes: file.size
    };

    this.appendFilesWithSizeLimit([attachment]);

    if (this.selectedFiles.length) {
      this.forceArrowUpdate();
    }
  }

}
